import { useInfiniteQuery, useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, invokeCapability, newIdempotencyKey } from '@nw/web-platform';
import type { Book, BookNote, CreateBookInput, LibraryStats, ReadingSession, RecordReadingInput, Shelf, UpdateBookInput } from '../public/index.js';

export type BookFilters = {
  q?: string;
  status?: string;
  shelf?: string;
  tag?: string;
  author?: string;
  sort?: 'recent' | 'title';
};

export const libKeys = {
  all: ['library'] as const,
  books: (f: BookFilters) => ['library', 'books', f] as const,
  book: (id: string) => ['library', 'book', id] as const,
  stats: ['library', 'stats'] as const,
  shelves: ['library', 'shelves'] as const,
  notes: (bookId: string) => ['library', 'notes', bookId] as const,
  sessions: (bookId: string) => ['library', 'sessions', bookId] as const,
};

export function useBooks(filters: BookFilters) {
  return useInfiniteQuery({
    queryKey: libKeys.books(filters),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
      if (pageParam) params.set('cursor', pageParam);
      params.set('limit', '60');
      const res = await api<Book[], { nextCursor: string | null }>(`/library/books?${params}`, { signal });
      return { items: res.data, nextCursor: res.meta?.nextCursor ?? null };
    },
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });
}

export function useBook(id: string | null) {
  return useQuery({
    queryKey: libKeys.book(id ?? ''),
    queryFn: async () => (await api<Book>(`/library/books/${id}`)).data,
    enabled: !!id,
    retry: false,
  });
}
export function useStats() {
  return useQuery({
    queryKey: libKeys.stats,
    queryFn: async () => (await api<LibraryStats>('/library/stats')).data,
  });
}
export function useShelves() {
  return useQuery({
    queryKey: libKeys.shelves,
    queryFn: async () => (await api<Shelf[]>('/library/shelves')).data,
  });
}
export function useNotes(bookId: string | null) {
  return useQuery({
    queryKey: libKeys.notes(bookId ?? ''),
    queryFn: async () => (await api<BookNote[]>(`/library/books/${bookId}/notes`)).data,
    enabled: !!bookId,
  });
}
export function useSessions(bookId: string | null) {
  return useQuery({
    queryKey: libKeys.sessions(bookId ?? ''),
    queryFn: async () => (await api<ReadingSession[]>(`/library/books/${bookId}/reading-sessions`)).data,
    enabled: !!bookId,
  });
}

function useLibraryInvalidate() {
  const qc = useQueryClient();
  return (book?: Book) => {
    if (book) qc.setQueryData(libKeys.book(book.id), book);
    void qc.invalidateQueries({ queryKey: ['library', 'books'] });
    void qc.invalidateQueries({ queryKey: libKeys.stats });
    void qc.invalidateQueries({ queryKey: libKeys.shelves });
    void qc.invalidateQueries({ queryKey: ['home'] });
    void qc.invalidateQueries({ queryKey: ['pending-projections'] });
  };
}

export function useCreateBook() {
  const done = useLibraryInvalidate();
  return useMutation({
    mutationFn: async (input: CreateBookInput) => (await api<Book>('/library/books', { body: input })).data,
    onSuccess: (b) => done(b),
  });
}
export function useUpdateBook() {
  const done = useLibraryInvalidate();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateBookInput }) =>
      (
        await api<Book>(`/library/books/${id}`, {
          method: 'PATCH',
          body: patch,
        })
      ).data,
    onSuccess: (b) => done(b),
  });
}
export function useDeleteBook() {
  const done = useLibraryInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (b: Book) =>
      api(`/library/books/${b.id}`, {
        method: 'DELETE',
        body: { expectedRevision: b.revision },
      }),
    onSuccess: (_, b) => {
      qc.removeQueries({ queryKey: libKeys.book(b.id) });
      done();
    },
  });
}
export function useRestoreBook() {
  const done = useLibraryInvalidate();
  return useMutation({
    mutationFn: async (id: string) => (await api<Book>(`/library/books/${id}/restore`, { method: 'POST' })).data,
    onSuccess: (b) => done(b),
  });
}

/** Each dialog keeps one idempotency key so a double-submit records one session. */
export function useRecordReading(bookId: string) {
  const done = useLibraryInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, key }: { input: RecordReadingInput; key: string }) =>
      await api<{ session: ReadingSession; book: Book }, { duplicate: boolean }>(`/library/books/${bookId}/reading-sessions`, {
        body: input,
        headers: { 'idempotency-key': key },
      }),
    onSuccess: (res) => {
      done(res.data.book);
      void qc.invalidateQueries({ queryKey: libKeys.sessions(bookId) });
    },
  });
}
export { newIdempotencyKey };

export function useSavePosition(bookId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { currentPage?: number | null; pdfPageIndex?: number | null; expectedRevision: number }) =>
      (
        await api<Book>(`/library/books/${bookId}/reading-state`, {
          method: 'PUT',
          body: input,
        })
      ).data,
    onSuccess: (b) => {
      qc.setQueryData(libKeys.book(b.id), b);
      void qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useNoteMutations(bookId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: libKeys.notes(bookId) });
    void qc.invalidateQueries({ queryKey: ['home'] });
    void qc.invalidateQueries({ queryKey: ['pending-projections'] });
  };
  return {
    create: useMutation({
      mutationFn: async (input: { body: string; page?: number | null; pdfPageIndex?: number | null; conceptKeys?: string[] }) => (await api<BookNote>(`/library/books/${bookId}/notes`, { body: input })).data,
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...patch }: { id: string; body?: string; conceptKeys?: string[]; expectedRevision: number }) =>
        (
          await api<BookNote>(`/library/notes/${id}`, {
            method: 'PATCH',
            body: patch,
          })
        ).data,
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: async (n: BookNote) =>
        api(`/library/notes/${n.id}`, {
          method: 'DELETE',
          body: { expectedRevision: n.revision },
        }),
      onSuccess: refresh,
    }),
  };
}

export function useShelfMutations() {
  const done = useLibraryInvalidate();
  return {
    create: useMutation({
      mutationFn: async (name: string) => (await api<Shelf>('/library/shelves', { body: { name } })).data,
      onSuccess: () => done(),
    }),
    remove: useMutation({
      mutationFn: async (id: string) => api(`/library/shelves/${id}`, { method: 'DELETE' }),
      onSuccess: () => done(),
    }),
    add: useMutation({
      mutationFn: async (v: { shelfId: string; bookId: string }) =>
        (
          await api<Book>(`/library/shelves/${v.shelfId}/items/${v.bookId}`, {
            method: 'PUT',
          })
        ).data,
      onSuccess: (b) => done(b),
    }),
    removeItem: useMutation({
      mutationFn: async (v: { shelfId: string; bookId: string }) =>
        (
          await api<Book>(`/library/shelves/${v.shelfId}/items/${v.bookId}`, {
            method: 'DELETE',
          })
        ).data,
      onSuccess: (b) => done(b),
    }),
  };
}

export async function uploadPdf(file: File) {
  const form = new FormData();
  form.append('file', file);
  return (
    await api<{ id: string; bytes: number; originalName: string }>('/assets', {
      form,
    })
  ).data;
}

/** Optional: Mind Networks concepts through the capability gateway (never Mind's own routes). */
export function searchConcepts(q: string) {
  return invokeCapability<Array<{ key: string; id: string; title: string }>>('library', 'mind.concepts.search@1', { q, limit: 12 });
}

export type BookCandidate = {
  providerWorkId: string;
  title: string;
  subtitle: string | null;
  authors: Array<{ name: string; providerId: string | null }>;
  firstPublishYear: number | null;
  isbns: string[];
  coverId: number | null;
  pageCountMedian: number | null;
  editionCount: number | null;
  publishers: string[];
};
export type BookEdition = {
  providerEditionId: string;
  providerWorkId: string;
  title: string;
  subtitle: string | null;
  publishers: string[];
  publishDate: string | null;
  pageCount: number | null;
  isbn13: string[];
  isbn10: string[];
  coverId: number | null;
  physicalFormat: string | null;
};
export async function lookupBooks(query: string) {
  return api<BookCandidate[], { mode: 'live' | 'fixture'; cached: boolean; attribution: string }>('/integrations/openlibrary/actions/search', { body: { query } });
}
export async function lookupEditions(workId: string) {
  return api<BookEdition[], { mode: 'live' | 'fixture'; attribution: string }>('/integrations/openlibrary/actions/editions', { body: { workId } });
}
